// src/pages/InvoiceDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { ItemSelect, QuickAddItemModal, NEW_ITEM_VALUE, itemLinkText, useSponsorLevels, LineSponsorLevel, itemTierId } from '../components/ItemPicker'; // [marker: item-list v1] [marker: fixes-oct21 v1]
import { AccountSelect } from '../components/AccountSelect'; // [marker: invoice-links v1]
import { ClassSelect } from '../components/ClassSelect'; // [marker: books-pro v1] class per line (blank = the event's, automatically)
import EmailComposer from '../components/EmailComposer'; // [marker: email-compose v1]
import ReceivePaymentModal, { METHOD_LABELS, fmtDay } from '../components/ReceivePaymentModal'; // [marker: payments-deposits v1]
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES_INVOICE = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS_INVOICE = [
  'header','bold','italic','underline','strike',
  'color','background','list','bullet','link',
];

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const { isManagerOrAbove, isSuperAdmin } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [editRecipient, setEditRecipient] = useState(false);
  const [refundPayment, setRefundPayment] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false); /* [marker: item-list v1] */

  const loadInvoice = () => {
    api.get(`/invoices/${id}`)
      .then(res => setInvoice(res.data))
      .catch(() => toast.error('Failed to load invoice.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadInvoice(); }, [id]);

  const handleSend = async () => {
    if (!window.confirm('Mark this invoice as sent?')) return;
    try {
      await api.post(`/invoices/${id}/send`);
      toast.success('Invoice marked as sent.');
      loadInvoice();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  const handleVoid = async () => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    try {
      await api.post(`/invoices/${id}/void`);
      toast.success('Invoice voided.');
      loadInvoice();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  // [marker: invoice-pdf-auth-blob v1]
  // window.open() on the API URL sends no Authorization header, so the PDF
  // endpoint rejected it with "Authentication required." Instead fetch the PDF
  // through the api helper (Bearer token attached) as a blob and open that.
  const handleGeneratePdf = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    let objectUrl = null;
    try {
      const res = await api.get(`/pdfs/invoices/${id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      objectUrl = window.URL.createObjectURL(blob);
      const win = window.open(objectUrl, '_blank');
      if (!win) {
        // Popup blocked — fall back to a direct download of the same blob.
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `${invoice?.invoice_number || 'invoice'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Release the object URL after the tab has had time to load it.
      setTimeout(() => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); }, 60000);
    } catch (err) {
      let msg = 'Failed to generate PDF.';
      // Blob error responses arrive as a Blob; try to read the JSON message.
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (_) {}
      } else {
        msg = err.response?.data?.error || msg;
      }
      toast.error(msg);
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    try {
      await api.post('/payments/payment-link', { invoiceId: parseInt(id) });
      toast.success('Payment link generated.');
      loadInvoice();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  const fmt = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 32, height: 32 }} /></div>;
  if (!invoice) return <div className="empty-state"><h3>Invoice not found</h3><Link to="/invoices">← Back</Link></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 4 }}>
            <Link to="/invoices" style={{ color: 'var(--gray-400)' }}>Invoices</Link> / {invoice.invoice_number}
          </div>
          <h1 className="page-title">{invoice.invoice_number}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <span className={`badge badge-${invoice.status}`}>{invoice.status?.replace('_',' ')}</span>
            <span className="text-muted" style={{ textTransform: 'capitalize' }}>{invoice.invoice_type?.replace('_',' ')}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleGeneratePdf} disabled={pdfLoading}>📄 {pdfLoading ? 'Generating…' : 'PDF'}</button>
          {/* [marker: invoice-edit-any v1] [marker: void-edit v1] editable in every status, including void */}
          {isManagerOrAbove() && (
            <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>Edit</button>
          )}
          {isManagerOrAbove() && invoice.status === 'draft' && (
            <button className="btn btn-secondary" onClick={handleSend}>Mark Sent</button>
          )}
          {isManagerOrAbove() && invoice.status !== 'paid' && invoice.status !== 'void' && (
            <>
              <button className="btn btn-primary" onClick={() => setShowEmailModal(true)}>✉ Send Email</button>
              <button className="btn btn-secondary" onClick={handleGeneratePaymentLink}>💳 Payment Link</button>
              <button className="btn btn-primary" onClick={() => setShowPayModal(true)}>+ Record Payment</button>
            </>
          )}
          {isSuperAdmin() && !['paid','void'].includes(invoice.status) && (
            <button className="btn btn-danger" onClick={handleVoid}>Void</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title">Bill To</span>
              {isManagerOrAbove() && (<button className="btn btn-ghost btn-sm" onClick={() => setEditRecipient(true)}>✎ Edit</button>)}
              {invoice.business_name && <Link to={`/members/${invoice.business_id}`} className="btn btn-ghost btn-sm">{invoice.business_name} →</Link>}
            </div>
            <div className="card-body">
              {invoice.business_name && <div style={{ fontWeight: 600 }}>{invoice.business_name}</div>}
              {invoice.contact_first_name && <div>{invoice.contact_first_name} {invoice.contact_last_name}</div>}
              {invoice.contact_email && <div style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>{invoice.contact_email}</div>}
              {!invoice.business_name && (invoice.recipient_first_name || invoice.recipient_email) && (
                <>
                  {(invoice.recipient_first_name || invoice.recipient_last_name) && (
                    <div style={{ fontWeight: 600 }}>{invoice.recipient_first_name} {invoice.recipient_last_name}</div>
                  )}
                  {invoice.recipient_email && (
                    <div style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>{invoice.recipient_email}</div>
                  )}
                  <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--gray-400)' }}>One-off recipient (not a member)</div>
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Line Items</span></div>
            <div className="table-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr><th>Description</th><th style={{ textAlign: 'center' }}>Qty</th><th style={{ textAlign: 'right' }}>Unit Price</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
                <tbody>
                  {invoice.lineItems?.map((item, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{item.name}</div>
                        {item.description && <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{item.description}</div>}
                        {/* [marker: invoice-links v1] what the line is tied to */}
                        {(item.catalog_item_id || item.income_account_name || item.sponsorship_tier_id) && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginTop: 2 }}>
                            {item.catalog_item_id ? <>Item: <Link to={`/products?item=${item.catalog_item_id}`}>{item.catalog_name || 'Item List entry'}</Link></> : null}
                            {/* [marker: fixes-oct21 v1] the line's own event + level */}
                            {(item.line_event_id || item.catalog_event_id) ? <> · Event: <Link to={`/events/${item.line_event_id || item.catalog_event_id}`}>{item.line_event_name || item.catalog_event_name || 'event'}</Link></> : null}
                            {(item.line_tier_name || item.catalog_tier_name) ? ` · Sponsorship: ${item.line_tier_name || item.catalog_tier_name}` : item.catalog_ticket_type_name ? ` · Ticket: ${item.catalog_ticket_type_name}` : ''}
                            {item.income_account_name ? ` · Income: ${item.income_account_name}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>{parseFloat(item.quantity)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(item.unit_price)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt(item.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '16px 20px', background: 'var(--gray-50)' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <table style={{ width: 280 }}>
                  <tbody>
                    <tr><td style={{ padding: '4px 0', color: 'var(--gray-600)' }}>Subtotal</td><td style={{ textAlign: 'right', padding: '4px 0' }}>{fmt(invoice.subtotal)}</td></tr>
                    {parseFloat(invoice.discount_amount) > 0 && (
                      <tr><td style={{ color: 'var(--success)' }}>Discount</td><td style={{ textAlign: 'right', color: 'var(--success)' }}>-{fmt(invoice.discount_amount)}</td></tr>
                    )}
                    <tr style={{ borderTop: '2px solid var(--navy)', fontWeight: 700, fontSize: '1rem' }}>
                      <td style={{ padding: '8px 0 4px' }}>Total</td>
                      <td style={{ textAlign: 'right', padding: '8px 0 4px' }}>{fmt(invoice.total_amount)}</td>
                    </tr>
                    {invoice.payments?.map((p, i) => (
                      <tr key={p.id ?? i}><td style={{ color: 'var(--success)', fontSize: '0.875rem' }}>Payment ({METHOD_LABELS[p.payment_method] || p.payment_method}{p.check_number ? ` #${p.check_number}` : ''}{p.check_date || p.paid_at ? ` · ${fmtDay(String(p.check_date || p.paid_at).slice(0, 10))}` : ''}){p.received_amount && Math.abs(parseFloat(p.received_amount) - parseFloat(p.amount)) > 0.004 ? <span style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}> — part of {fmt(p.received_amount)}</span> : null}{p.cardknox_transaction_id ? <span style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }} title="From Cardknox: name on card · last 4 · auth code · reference #"> · 💳 {[p.card_name, p.card_last4 && `····${p.card_last4}`, p.card_auth_code && `auth ${p.card_auth_code}`, `ref ${p.cardknox_transaction_id}`].filter(Boolean).join(' · ')}</span> : null}{/* [marker: card-details v1] */}{p.received_status === 'pending' ? <span style={{ color: '#b45309', fontSize: '0.75rem' }}> · not deposited yet</span> : p.deposited_date ? <span style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}> · deposited {fmtDay(p.deposited_date)}</span> : null}{p.id && (<button onClick={() => setEditPayment(p)} title="Edit / correct this payment" style={{ marginLeft: 6, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '0.8rem', padding: 0 }}>✏️</button>)}{isSuperAdmin() && p.id && p.cardknox_transaction_id && (<button onClick={() => setRefundPayment(p)} title="Refund this payment" style={{ marginLeft: 4, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '0.8rem', padding: 0 }}>↩</button>)}</td><td style={{ textAlign: 'right', color: 'var(--success)', fontSize: '0.875rem' }}>{fmt(p.amount)}</td></tr>
                    ))}
                    <tr style={{ color: parseFloat(invoice.balance_due) > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 700 }}>
                      <td style={{ padding: '4px 0' }}>{parseFloat(invoice.balance_due) < 0 ? 'Credit (overpaid)' : 'Balance Due'}</td>
                      <td style={{ textAlign: 'right', padding: '4px 0' }}>{fmt(Math.abs(parseFloat(invoice.balance_due) || 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><span className="card-title">Details</span></div>
            <div className="card-body">
              <InfoRow label="Issue Date" value={fmtDate(invoice.issue_date)} />
              <InfoRow label="Due Date"   value={fmtDate(invoice.due_date)} />
              <InfoRow label="Sent"       value={invoice.sent_at ? fmtDate(invoice.sent_at) : null} />
              <InfoRow label="Paid"       value={invoice.paid_at ? fmtDate(invoice.paid_at) : null} />
              <InfoRow label="Salesperson" value={invoice.salesperson_name} />
            </div>
          </div>

          {/* Payment link — automatic or pasted by staff [marker: item-list v1] */}
          {(invoice.cardknox_payment_link || (isManagerOrAbove() && invoice.status !== 'void')) && (
            <div className="card" style={{ padding: 16, background: 'var(--info-bg)', border: '1px solid #bfdbfe' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--info)' }}>
                  Payment Link
                  {invoice.cardknox_payment_link && (
                    <span className={`badge ${invoice.payment_link_custom ? 'badge-warning' : 'badge-info'}`} style={{ marginLeft: 6, fontSize: '0.65rem' }}>
                      {invoice.payment_link_custom ? 'Custom' : 'Automatic'}
                    </span>
                  )}
                </div>
                {isManagerOrAbove() && invoice.status !== 'void' && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowLinkModal(true)}>✎ Change</button>
                )}
              </div>
              {invoice.cardknox_payment_link ? (
                <a href={invoice.cardknox_payment_link} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', wordBreak: 'break-all', color: 'var(--info)' }}>
                  {invoice.cardknox_payment_link}
                </a>
              ) : (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>No link yet. Use “💳 Payment Link” for an automatic one, or ✎ Change to paste your own.</div>
              )}
              {invoice.payment_link_custom && (
                <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginTop: 6 }}>
                  Custom links are used as-is in invoice emails and aren’t changed when the invoice is edited. Payments made through a non-Cardknox link must be recorded by hand.
                </div>
              )}
            </div>
          )}

          {/* Change history — [marker: invoice-edit-any v1] */}
          {invoice.history?.length > 0 && (
            <div className="card" style={{ marginTop: 16, padding: 16 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 8 }}>Change History</div>
              {invoice.history.map(h => (
                <div key={h.id} style={{ borderTop: '1px solid var(--gray-100)', padding: '8px 0', fontSize: '0.8rem' }}>
                  <div style={{ color: 'var(--gray-500)', marginBottom: 4 }}>
                    {h.at ? new Date(h.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    {h.by ? ' · ' + h.by : ''}
                  </div>
                  {(h.changes || []).map((c, i) => (
                    <div key={i} style={{ color: 'var(--gray-700)', marginBottom: 2 }}>
                      <strong>{c.field}:</strong>{' '}
                      <span style={{ color: 'var(--gray-400)', textDecoration: 'line-through' }}>{c.from}</span>{' → '}
                      <span>{c.to}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {invoice.notes && (
            <div className="card" style={{ marginTop: 16, padding: 16 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 6 }}>Notes</div>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>{invoice.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* [marker: payments-deposits v1] QuickBooks-style Receive Payment (one check can pay several invoices) */}
      {showPayModal && (
        <ReceivePaymentModal
          presetInvoice={{
            id: invoice.id, business_id: invoice.business_id, business_name: invoice.business_name,
            recipient_name: [invoice.recipient_first_name, invoice.recipient_last_name].filter(Boolean).join(' '),
            balance_due: invoice.balance_due,
          }}
          onClose={() => setShowPayModal(false)}
          onSaved={() => { setShowPayModal(false); loadInvoice(); }}
        />
      )}

      {editPayment && (editPayment.received_payment_id ? (
        <ReceivePaymentModal
          receiptId={editPayment.received_payment_id}
          onClose={() => setEditPayment(null)}
          onSaved={() => { setEditPayment(null); loadInvoice(); }}
        />
      ) : (
        <EditPaymentModal
          payment={editPayment}
          onClose={() => setEditPayment(null)}
          onSaved={() => { setEditPayment(null); loadInvoice(); }}
        />
      ))}
      {refundPayment && (
        <RefundPaymentModal
          payment={refundPayment}
          onClose={() => setRefundPayment(null)}
          onSaved={() => { setRefundPayment(null); loadInvoice(); }}
        />
      )}


      {editRecipient && (
        <EditRecipientModal
          invoice={invoice}
          onClose={() => setEditRecipient(false)}
          onSaved={() => { setEditRecipient(false); loadInvoice(); }}
        />
      )}

      {showLinkModal && (
        <PaymentLinkModal
          invoice={invoice}
          onClose={() => setShowLinkModal(false)}
          onSaved={() => { setShowLinkModal(false); loadInvoice(); }}
        />
      )}

      {showEditModal && (
        <EditInvoiceModal
          invoice={invoice}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); loadInvoice(); }}
        />
      )}

      {showEmailModal && (
        <EmailComposer
          kind="invoice" id={id}
          title={`Email Invoice ${invoice.invoice_number}`}
          sendLabel="Send Invoice"
          onClose={() => setShowEmailModal(false)}
          onSent={() => { setShowEmailModal(false); loadInvoice(); }}
        />
      )}
    </div>
  );
}

// ─── SendInvoiceEmailModal (Phase 13: preview-then-send) ─────────────────────
function SendInvoiceEmailModal({ invoiceId, fallbackEmail, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState(''); /* [marker: invoice-email-cc v1] */
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/invoice-email/${invoiceId}/preview`)
      .then(res => {
        setTo(res.data.to || fallbackEmail || '');
        setCc(res.data.cc || ''); /* [marker: invoice-email-cc v1] */
        setSubject(res.data.subject || '');
        setHtml(res.data.html || '');
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load preview.'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const handleSend = async () => {
    if (!to.trim() || !subject.trim() || !html.trim()) {
      toast.error('To, subject, and body are all required.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/invoice-email/${invoiceId}/send`, { to: to.trim(), cc: cc.trim(), subject: subject.trim(), html });
      toast.success('Invoice email sent.');
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Send Invoice Email</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-page" style={{ minHeight: 220 }}><div className="spinner" /></div>
          ) : error ? (
            <div style={{
              padding: 16, background: '#fef2f2', border: '1px solid var(--danger)',
              borderRadius: 6, color: 'var(--danger)',
            }}>
              {error}
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">To *</label>
                <input className="form-input" type="email" value={to} onChange={e => setTo(e.target.value)} />
                <p className="form-hint">Edit the recipient if needed before sending.</p>
              </div>
              {/* [marker: invoice-email-cc v1] optional second recipient */}
              <div className="form-group">
                <label className="form-label">CC (optional)</label>
                <input className="form-input" type="email" value={cc} onChange={e => setCc(e.target.value)} placeholder="Send a copy to a second person" />
                <p className="form-hint">
                  {cc
                    ? 'Both people receive the same one email. Clear this to send to just the main recipient.'
                    : "Pre-filled from the contact's secondary email when there is one. Leave blank to send to just the main recipient."}
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Body *</label>
                <div style={{ background: 'white', borderRadius: 6 }}>
                  <ReactQuill
                    theme="snow"
                    value={html}
                    onChange={setHtml}
                    modules={QUILL_MODULES_INVOICE}
                    formats={QUILL_FORMATS_INVOICE}
                    style={{ minHeight: 280 }}
                  />
                </div>
                <p className="form-hint">
                  Edits here only affect this single email. The underlying template is not changed.
                  Sending will mark this invoice as "sent."
                </p>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={loading || sending || !!error}>
            {sending ? 'Sending…' : '✉ Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RefundPaymentModal({ payment, onClose, onSaved }) {
  const [amount, setAmount] = useState(payment && payment.amount != null ? String(payment.amount) : '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const body = { paymentId: payment.id, reason };
      const amt = parseFloat(amount);
      if (!isNaN(amt) && amt > 0) body.amount = amt;
      const res = await api.post('/payments/refund', body);
      toast.success(`Refunded ${res.data.amountRefunded != null ? res.data.amountRefunded : amount}.`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Refund failed.');
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <span className="modal-title">Refund Payment</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>This issues a real Cardknox refund for card payments. Cash/check payments should be edited or deleted instead.</p>
          <div className="form-group">
            <label className="form-label">Amount</label>
            <input className="form-input" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} />
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>Leave as-is for a full refund of this payment.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Reason (optional)</label>
            <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. duplicate charge" />
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-danger" onClick={submit} disabled={saving}>{saving ? 'Refunding…' : 'Issue Refund'}</button>
        </div>
      </div>
    </div>
  );
}

function RecordPaymentModal({ invoiceId, balanceDue, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: balanceDue || '', paymentMethod: 'check', referenceNumber: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSave = async () => {
    if (!form.amount) return toast.error('Amount is required.');
    setSaving(true);
    try {
      await api.post(`/invoices/${invoiceId}/payments`, form);
      toast.success('Payment recorded.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Record Payment</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input className="form-input" type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select className="form-select" value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="wire">Wire / ACH</option>
              <option value="cardknox">Cardknox (online)</option>
              <option value="comp">Comp</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Reference # (check number, etc.)</label>
            <input className="form-input" value={form.referenceNumber} onChange={e => set('referenceNumber', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Record Payment'}</button>
        </div>
      </div>
    </div>
  );
}

function EditRecipientModal({ invoice, onClose, onSaved }) {
  const [form, setForm] = useState({
    recipientFirstName: invoice.recipient_first_name || invoice.contact_first_name || '',
    recipientLastName:  invoice.recipient_last_name  || invoice.contact_last_name  || '',
    recipientEmail:     invoice.recipient_email      || invoice.contact_email      || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isMember = !!invoice.business_name;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/invoices/${invoice.id}/recipient`, {
        recipientFirstName: form.recipientFirstName,
        recipientLastName:  form.recipientLastName,
        recipientEmail:     form.recipientEmail,
      });
      toast.success('Recipient updated.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Recipient</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {isMember && (
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', background: 'var(--info-bg)', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              This invoice is linked to {invoice.business_name}. The name normally shown comes from the member's contact record; what you set here saves a one-off recipient on this invoice.
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">First Name</label>
              <input className="form-input" value={form.recipientFirstName} onChange={e => set('recipientFirstName', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Last Name</label>
              <input className="form-input" value={form.recipientLastName} onChange={e => set('recipientLastName', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.recipientEmail} onChange={e => set('recipientEmail', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}


function EditPaymentModal({ payment, onClose, onSaved }) {
  const isCard = !!payment.cardknox_transaction_id;
  const [form, setForm] = useState({
    amount:          payment.amount ?? '',
    paymentMethod:   payment.payment_method || 'check',
    referenceNumber: payment.reference_number || '',
    paidAt:          payment.paid_at ? String(payment.paid_at).substring(0, 10) : '',
    notes:           payment.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.amount) return toast.error('Amount is required.');
    setSaving(true);
    try {
      await api.patch(`/payments/${payment.id}`, {
        amount:          form.amount,
        paymentMethod:   form.paymentMethod,
        referenceNumber: form.referenceNumber,
        paidAt:          form.paidAt || null,
        notes:           form.notes,
      });
      toast.success('Payment updated.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this payment? The invoice balance will be recalculated.')) return;
    setDeleting(true);
    try {
      await api.delete(`/payments/${payment.id}`);
      toast.success('Payment deleted.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Payment</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {isCard && (
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', background: 'var(--info-bg)', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              This is a card (Cardknox) payment. You can fix the reference, date, and notes here, but to change or reverse the amount use <strong>Refund</strong>.
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input className="form-input" type="number" step="0.01" value={form.amount}
              onChange={e => set('amount', e.target.value)} disabled={isCard} />
          </div>
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select className="form-select" value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="wire">Wire / ACH</option>
              <option value="cardknox">Cardknox (online)</option>
              <option value="comp">Comp</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Reference # (check number, etc.)</label>
            <input className="form-input" value={form.referenceNumber} onChange={e => set('referenceNumber', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Payment Date</label>
            <input className="form-input" type="date" value={form.paidAt} onChange={e => set('paidAt', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={handleDelete} disabled={deleting || saving}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || deleting}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditInvoiceModal({ invoice, onClose, onSaved }) {
  const [form, setForm] = useState({
    businessId:     invoice.business_id ? String(invoice.business_id) : '', /* [marker: invoice-edit-any v1] */
    invoiceType:    invoice.invoice_type || 'general',
    issueDate:      invoice.issue_date ? String(invoice.issue_date).slice(0, 10) : '',
    dueDate:        invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
    contactId:      invoice.contact_id || '',
    salespersonId:  invoice.salesperson_id || '',
    notes:          invoice.notes || '',
    discountAmount: invoice.discount_amount != null ? String(invoice.discount_amount) : '0',
  });
  const [lineItems, setLineItems] = useState(
    (invoice.lineItems || invoice.line_items || []).map(li => ({
      catalogItemId: li.catalog_item_id || '',
      name:          li.name || '',
      description:   li.description || '',
      unitPrice:     li.unit_price != null ? String(li.unit_price) : '',
      quantity:      li.quantity != null ? String(li.quantity) : '1',
      incomeAccountId: li.income_account_id || null, // [marker: invoice-links v1]
      sponsorshipTierId: li.sponsorship_tier_id || null, // [marker: fixes-oct21 v1]
      classId: li.class_id || null,
    }))
  );
  const [contacts, setContacts] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const sponsorLevels = useSponsorLevels(); // [marker: fixes-oct21 v1]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lineItems.length === 0) {
      setLineItems([{ name: '', description: '', unitPrice: '', quantity: '1', catalogItemId: '', incomeAccountId: null }]);
    }
    Promise.all([
      api.get('/lookups/salespersons'),
      api.get('/invoice-items?is_active=all'), // [marker: invoice-links v1] any item, active or not
      api.get('/businesses?limit=1000').catch(() => ({ data: [] })),
      api.get('/invoices/types').catch(() => ({ data: [] })),
    ]).then(([spRes, catRes, bizRes, typesRes]) => {
      setSalespersons(spRes.data || []);
      setCatalogItems(catRes.data?.data || catRes.data || []);
      setBusinesses(bizRes.data?.data || bizRes.data?.businesses || (Array.isArray(bizRes.data) ? bizRes.data : []));
      setInvoiceTypes((typesRes.data || []).map(t => ({ value: t.type_key, label: t.label })));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contacts follow the chosen business
  useEffect(() => {
    if (!form.businessId) { setContacts([]); return; }
    api.get(`/businesses/${form.businessId}`)
      .then(r => setContacts(r.data?.contacts || []))
      .catch(() => setContacts([]));
  }, [form.businessId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addLine = () => setLineItems(items => [...items, { name: '', description: '', unitPrice: '', quantity: '1', catalogItemId: '', incomeAccountId: null }]);
  const removeLine = (i) => setLineItems(items => items.length > 1 ? items.filter((_, idx) => idx !== i) : items);
  const updateLine = (i, k, v) => setLineItems(items => items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const [addItemRow, setAddItemRow] = useState(null); /* [marker: item-list v1] */
  const applyCatalog = (i, catId, itemOverride) => {
    if (catId === NEW_ITEM_VALUE) { setAddItemRow(i); return; }
    if (!catId) { updateLine(i, 'catalogItemId', ''); return; }
    const cat = itemOverride || catalogItems.find(c => String(c.id) === String(catId));
    if (!cat) return;
    setLineItems(items => items.map((it, idx) => idx === i ? {
      ...it,
      catalogItemId: catId,
      name:          cat.name || it.name,
      description:   cat.description || it.description,
      unitPrice:     (cat.default_price ?? cat.unit_price) != null ? String(cat.default_price ?? cat.unit_price) : it.unitPrice, /* [marker: sponsorship-products v1] */
      incomeAccountId: cat.income_account_id || it.incomeAccountId || null,
    } : it));
  };

  const subtotal = lineItems.reduce((s, it) => s + (parseFloat(it.unitPrice) || 0) * (parseFloat(it.quantity) || 0), 0);
  const discount = parseFloat(form.discountAmount) || 0;
  const total = Math.max(0, subtotal - discount);
  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;
  const alreadyPaid = parseFloat(invoice.amount_paid) || 0;
  const isVoid = invoice.status === 'void'; // [marker: void-edit v1] a voided invoice stays void, balance $0
  const newBalance = isVoid ? 0 : total - alreadyPaid;
  const isLocked = invoice.status !== 'draft';

  const handleSave = async () => {
    if (!form.dueDate) return toast.error('Due date is required.');
    if (!form.issueDate) return toast.error('Issue date is required.');
    const valid = lineItems.some(it => it.name.trim() && it.unitPrice !== '' && parseFloat(it.unitPrice) >= 0);
    if (!valid) return toast.error('At least one line item with a name and price is required.');
    if (discount > subtotal) return toast.error('The discount is larger than the subtotal.');
    if (isLocked && !isVoid && Math.abs(total - (parseFloat(invoice.total_amount) || 0)) > 0.004) {
      const msg = newBalance < 0
        ? `The new total (${fmt(total)}) is less than what was already paid (${fmt(alreadyPaid)}). The invoice will show a ${fmt(-newBalance)} credit. Nothing is refunded automatically. Continue?`
        : `The total changes from ${fmt(invoice.total_amount)} to ${fmt(total)}. Balance due will be ${fmt(newBalance)}. Continue?`;
      if (!window.confirm(msg)) return;
    }

    const cleanItems = lineItems
      .filter(it => it.name.trim())
      .map(it => ({
        catalogItemId: it.catalogItemId || null,
        name:          it.name.trim(),
        description:   it.description?.trim() || null,
        unitPrice:     parseFloat(it.unitPrice),
        quantity:      parseFloat(it.quantity) || 1,
        incomeAccountId: it.incomeAccountId || null,
        sponsorshipTierId: itemTierId(catalogItems.find(c => String(c.id) === String(it.catalogItemId))) ? null : (it.sponsorshipTierId || null), // [marker: fixes-oct21 v1]
        classId: it.classId || null,
      }));

    setSaving(true);
    try {
      await api.patch(`/invoices/${invoice.id}`, {
        businessId:     form.businessId ? parseInt(form.businessId) : null,
        invoiceType:    form.invoiceType,
        issueDate:      form.issueDate,
        dueDate:        form.dueDate,
        contactId:      form.contactId ? parseInt(form.contactId) : null,
        salespersonId:  form.salespersonId ? parseInt(form.salespersonId) : null,
        notes:          form.notes || null,
        discountAmount: discount,
        lineItems:      cleanItems,
      }).then(r => {
        if (r.data?.membershipNowPaid) {
          toast('This membership invoice is now fully paid. Renewal is not automatic after an edit — renew the membership from the member page if it hasn\'t been already.', { duration: 9000 });
        }
      });
      toast.success('Invoice updated.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update invoice.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Invoice {invoice.invoice_number}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {isVoid && (
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-700)', background: 'var(--gray-100)', border: '1px solid var(--gray-300)', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              This invoice is <strong>void</strong>. You can change anything — it stays void and its balance stays $0.00. Every change is logged in Change History.
            </div>
          )}
          {isLocked && !isVoid && (
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-700)', background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              This invoice is <strong>{invoice.status.replace('_', ' ')}</strong>. You can change anything. Payments already recorded ({fmt(alreadyPaid)}) are kept, and the balance and status are recalculated. Every change is logged in Change History.
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Business / Member</label>
              <select className="form-select" value={form.businessId} onChange={e => setForm(f => ({ ...f, businessId: e.target.value, contactId: '' }))}>
                <option value="">— None (one-off recipient) —</option>
                {invoice.business_id && !businesses.some(b => String(b.id) === String(invoice.business_id)) && (
                  <option value={String(invoice.business_id)}>{invoice.business_name || 'Current business'}</option>
                )}
                {businesses.map(b => <option key={b.id} value={b.id}>{b.business_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Invoice Type</label>
              <select className="form-select" value={form.invoiceType} onChange={e => set('invoiceType', e.target.value)}>
                {!invoiceTypes.some(t => t.value === form.invoiceType) && (
                  <option value={form.invoiceType}>{String(form.invoiceType).replace(/_/g, ' ')}</option>
                )}
                {invoiceTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Issue Date *</label>
              <input className="form-input" type="date" value={form.issueDate} onChange={e => set('issueDate', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Contact</label>
              <select className="form-select" value={form.contactId} onChange={e => set('contactId', e.target.value)}>
                <option value="">— No specific contact —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date *</label>
              <input className="form-input" type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Salesperson</label>
              <select className="form-select" value={form.salespersonId} onChange={e => set('salespersonId', e.target.value)}>
                <option value="">— None —</option>
                {salespersons.map(sp => <option key={sp.id} value={sp.id}>{sp.full_name || sp.initials}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 8 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>Line Items</h3>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>+ Add Line</button>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '24%' }}>Item</th>
                  <th>Name *</th>
                  <th>Description</th>
                  <th style={{ width: 160 }}>Income Account</th>
                  <th style={{ width: 110 }}>Unit Price *</th>
                  <th style={{ width: 80 }}>Qty</th>
                  <th style={{ width: 100 }}>Total</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((it, i) => {
                  const lineTotal = (parseFloat(it.unitPrice) || 0) * (parseFloat(it.quantity) || 0);
                  return (
                    <tr key={i}>
                      <td>
                        <ItemSelect items={catalogItems} value={it.catalogItemId} onChange={v => applyCatalog(i, v)} />
                        {it.catalogItemId && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginTop: 3 }}>
                            {itemLinkText(catalogItems.find(c => String(c.id) === String(it.catalogItemId))) || 'Stand-alone item'}
                          </div>
                        )}
                        <LineSponsorLevel line={it} item={catalogItems.find(c => String(c.id) === String(it.catalogItemId))} invoiceType={form.invoiceType}
                                          tiers={sponsorLevels} onChange={v => updateLine(i, 'sponsorshipTierId', v)} />
                      </td>
                      <td><input className="form-input" value={it.name} onChange={e => updateLine(i, 'name', e.target.value)} /></td>
                      <td><input className="form-input" value={it.description} onChange={e => updateLine(i, 'description', e.target.value)} /></td>
                      <td><AccountSelect types={['income', 'other_income']} value={it.incomeAccountId} onChange={v => updateLine(i, 'incomeAccountId', v)} placeholder="— From item —" />
                        <ClassSelect value={it.classId} onChange={v => updateLine(i, 'classId', v)} placeholder="Class: automatic" style={{ marginTop: 4, height: 30, fontSize: '0.8rem', padding: '2px 6px' }} /></td>
                      <td><input className="form-input" type="number" min="0" step="0.01" value={it.unitPrice} onChange={e => updateLine(i, 'unitPrice', e.target.value)} /></td>
                      <td><input className="form-input" type="number" min="0" step="1" value={it.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(lineTotal)}</td>
                      <td>
                        <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeLine(i)} disabled={lineItems.length === 1}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <div style={{ minWidth: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>Subtotal</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                <span>Discount</span>
                <input className="form-input" type="number" min="0" step="0.01" value={form.discountAmount} onChange={e => set('discountAmount', e.target.value)} style={{ width: 110, textAlign: 'right' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--gray-200)', fontWeight: 700, marginTop: 4 }}>
                <span>Total</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
              </div>
              {alreadyPaid > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: 'var(--success)', fontSize: '0.875rem' }}>
                    <span>Already paid</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>-{fmt(alreadyPaid)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontWeight: 700, color: newBalance > 0.004 ? 'var(--danger)' : 'var(--success)' }}>
                    <span>{newBalance < -0.004 ? 'Credit (overpaid)' : 'New balance due'}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(Math.abs(newBalance))}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
      {addItemRow !== null && (
        <QuickAddItemModal
          initial={lineItems[addItemRow]}
          onClose={() => setAddItemRow(null)}
          onCreated={(item) => {
            setCatalogItems(list => [...list, item]);
            applyCatalog(addItemRow, String(item.id), item);
            setAddItemRow(null);
          }}
        />
      )}
    </div>
  );
}

// ─── PaymentLinkModal — paste a custom link or go back to automatic ─────────
// [marker: item-list v1]
function PaymentLinkModal({ invoice, onClose, onSaved }) {
  const [link, setLink] = useState(invoice.payment_link_custom ? (invoice.cardknox_payment_link || '') : '');
  const [saving, setSaving] = useState(false);

  const save = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/invoices/${invoice.id}/payment-link`, body);
      toast.success(body.reset ? 'Back to the automatic payment link.' : 'Custom payment link saved.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the link.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Payment Link</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Use my own link</label>
            <input className="form-input" value={link} onChange={e => setLink(e.target.value)} placeholder="https://…" />
            <p className="form-hint">
              This link replaces the automatic one on the invoice page, in invoice emails and on the member portal.
              It is kept as-is if the invoice amount changes.
            </p>
          </div>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-ghost" onClick={() => save({ reset: true })} disabled={saving}
                  title="Build a Cardknox link for the current balance">↺ Use automatic link</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={() => save({ link })} disabled={saving || !link.trim()}>
              {saving ? 'Saving…' : 'Save Link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--gray-500)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
